// header és footer fáj 

// React és React Router importok
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
// Context és komponens importok
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HomePage from './pages/HomePage';
import Booking from './pages/Booking';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import MyBookings from './pages/MyBookings';
import UserDropdown from './components/UserDropdown';
import AdminGuard from './components/AdminGuard';
import AdminDashboard from './pages/AdminDashboard';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import SuperAdminPanel from './pages/SuperAdminPanel';
import { AlertProvider } from './contexts/AlertContext';

// Profil route védelem: Rendszergazda kizárása a profil oldalról
function ProfileRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/bejelentkezes" replace />;
  if (user.role === 'Rendszergazda') return <Navigate to="/" replace />;
  return children;
}

// Vendég route: Csak kijelentkezett felhasználóknak (pl. login, regisztráció)
function GuestRoute({ children }) {
  const { user } = useAuth();
  if (!user) return children;
  if (user.role === 'Rendszergazda') return <Navigate to="/rendszergazda-panel" replace />;
  if (user.role === 'Fodrász') return <Navigate to="/fodrasz-panel" replace />;
  return <Navigate to="/HomePage" replace />;
}

// A rendszergazda kizárólag a szuperadmin felületet érheti el
function SuperAdminRouteGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (user?.role === 'Rendszergazda' && location.pathname !== '/rendszergazda-panel') {
    return <Navigate to="/rendszergazda-panel" replace />;
  }

  return children;
}

// Fő UI tartalom és útvonalválasztó (Router)
function AppContent() {
  const { siteName, siteTitle } = useAuth();

  useEffect(() => {
    document.title = siteTitle;
  }, [siteTitle]);
  
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b0f19] transition-colors duration-300">
      {/* Navigációs sáv / Fejléc */}
      <nav className="bg-white dark:bg-gray-800 sticky top-0 z-50 border-b border-gray-200 dark:border-gray-700 shadow-md transition-colors">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-5">
          <div className="flex flex-wrap justify-between items-center gap-3 sm:gap-6">
            
            {/* Logó */}
            <div className="flex items-center gap-3 sm:gap-6 min-w-0">
              <Link to="/" className="group">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black bg-purple-600 dark:bg-purple-600 bg-clip-text text-transparent drop-shadow-lg group-hover:scale-105 transition-all whitespace-nowrap">
                  ✂️ {siteName}
                </h1>
              </Link>
            </div>

            {/* Felhasználói menü / Auth gombok */}
            <AuthMenu />
          </div>
        </div>
      </nav>

      {/* Fő tartalom / Útvonalak definíciója */}
      <main className="pt-6 pb-16 px-4">
        <SuperAdminRouteGuard>
          <Routes>
            {/* Publikus útvonalak */}
            <Route path="/" element={<HomePage />} />
            <Route path="/fooldal" element={<HomePage />} />
            <Route path="/kapcsolat" element={<Contact />} /> 
            <Route path="/adatvedelem" element={<Privacy />} />
            
            {/* A foglalási oldal vendégek számára is megnyitható; a mentéskor ellenőriz */}
            <Route path="/foglalas" element={<Booking />} />
            <Route path="/profil" element={<ProfileRoute><Profile /></ProfileRoute>} />
            <Route path="/sajat-fogalalsok" element={<ProfileRoute><MyBookings /></ProfileRoute>} />

            {/* Fodrász adminisztrációs felület */}
            <Route 
              path="/fodrasz-panel" 
              element={
                <AdminGuard allowedRoles={['Fodrász']}>
                  <AdminDashboard />
                </AdminGuard>
              } 
            />

            {/* Rendszergazda (Szuperadmin) felület */}
            <Route 
              path="/rendszergazda-panel" 
              element={
                <AdminGuard allowedRoles={['Rendszergazda']}>
                  <SuperAdminPanel />
                </AdminGuard>
              } 
            />
            
            {/* Hitelesítési oldalak (csak kijelentkezve) */}
            <Route path="/bejelentkezes" element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/regisztracio" element={<GuestRoute><Register /></GuestRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* 404 / Ismeretlen útvonal kezelése */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SuperAdminRouteGuard>
      </main>

      {/* Lábléc (Footer) */}
      <footer className="bg-gradient-to-r from-gray-900/95 to-gray-800/95 dark:from-black dark:to-gray-900 backdrop-blur-xl text-white py-12 border-t border-gray-800/50 dark:border-gray-700 transition-colors">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="mb-8">
           <h3 className="text-4xl font-bold mb-4 bg-purple-600 dark:bg-purple-600 bg-clip-text text-transparent">
              {siteName}
            </h3>
            <p className="text-gray-400 dark:text-gray-500 text-s sm:text-base">
              {new Date().getFullYear()} {siteName}. Minden jog fenntartva.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-lg text-gray-400 dark:text-gray-500">
            <Link to="/" className="hover:text-white dark:hover:text-gray-300 transition-colors">Főoldal</Link>
            <Link to="/adatvedelem" className="hover:text-white dark:hover:text-gray-300 transition-colors">Adatvédelem</Link>
            <Link to="/kapcsolat" className="hover:text-white dark:hover:text-gray-300 transition-colors">Kapcsolat</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Fejléc jobb oldali menüje (Téma váltó + Bejelentkezés/Profil)
function AuthMenu() {
  const { user, darkMode, toggleDarkMode } = useAuth();

  return (
    <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 ml-auto">
      {/* Sötét mód / Világos mód kapcsoló */}
      <button
        onClick={toggleDarkMode}
            className={`w-12 h-12 sm:w-12 sm:h-12 flex-shrink-0 flex items-center justify-center rounded-xl transition-all shadow-lg ${
          darkMode 
            ? 'bg-gray-700 text-yellow-300 hover:bg-gray-600' 
            : 'bg-blue-200 text-blue-600 hover:bg-blue-200'
        }`}
        title={darkMode ? 'Világos mód' : 'Sötét mód'}
      >
        {darkMode ? '🌙' : '☀️'}
      </button>

      {/* Profil legördülő vagy bejelentkezés gombok */}
      {user ? (
        <UserDropdown />
      ) : (
        <>
          <Link 
            to="/bejelentkezes" 
            className="px-3 sm:px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/20 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all text-xs sm:text-sm lg:text-base whitespace-nowrap"
          >
            Bejelentkezés
          </Link>
          <Link 
            to="/regisztracio"
            className="px-3 sm:px-6 py-2.5 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 dark:from-pink-600 dark:to-orange-600 dark:hover:from-pink-700 dark:hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all text-xs sm:text-sm lg:text-base whitespace-nowrap"
          >
            Regisztráció
          </Link>
        </>
      )}
    </div>
  );
}

// FŐ App Belépési pont (Context Provider + Router)
function App() {
  return (
    <AuthProvider>
      <AlertProvider>
        <Router>
          <AppContent />
        </Router>
      </AlertProvider>
    </AuthProvider>
  );
}

export default App;